// Read-only view of a stored measurement fetched from /api/results/{id}. Renders
// either a run set (with the localisation ladder + verdict) or a sweep.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchResult } from '@/lib/api';
import { parseSharePayload, type SharePayload } from '@/lib/share';
import { buildLadder } from '@/engine/metrics';
import { Heatmap, LossVsSize, RttVsPps, SegmentLadder } from '@/charts';
import { timestamp } from '@/lib/format';
import { EmptyState, Panel } from './components';
import { RunReport } from './results';

function SharedRun({ payload }: { payload: Extract<SharePayload, { kind: 'run' }> }) {
  const { t } = useTranslation();
  const nodeById = new Map(payload.nodes.map((n) => [n.id, n]));
  const multiTier = new Set(payload.runs.map((r) => r.tier)).size > 1;
  const ladder = buildLadder(payload.runs);
  return (
    <div className="results">
      {multiTier && payload.verdict && ladder.length > 0 && (
        <Panel title={t('ladder.title')}>
          <SegmentLadder rungs={ladder} verdict={payload.verdict} />
          <p className="hint">{t('ladder.note')}</p>
        </Panel>
      )}
      {payload.runs.map((run) => (
        <RunReport key={run.nodeId} run={run} node={nodeById.get(run.nodeId)} />
      ))}
    </div>
  );
}

function SharedSweep({ payload }: { payload: Extract<SharePayload, { kind: 'sweep' }> }) {
  const { t } = useTranslation();
  return (
    <div className="results">
      <Panel title={t('sweep.heatmaps')}>
        <Heatmap sweep={payload.sweep} axis="size" nodes={payload.nodes} />
      </Panel>
      <div className="chartgrid chartgrid--2">
        <Panel title={t('sweep.lossVsSize')}>
          <LossVsSize sweep={payload.sweep} nodes={payload.nodes} />
        </Panel>
        <Panel title={t('sweep.rttVsPps')}>
          <RttVsPps sweep={payload.sweep} nodes={payload.nodes} />
        </Panel>
      </div>
    </div>
  );
}

export function SharePage({ id }: { id: string | null }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<SharePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setError(t('share.missing'));
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchResult<unknown>(id)
      .then((blob) => {
        if (cancelled) return;
        setPayload(parseSharePayload(blob));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  return (
    <div className="page share">
      <div className="share__head">
        <h1>{payload?.title ?? t('share.title')}</h1>
        <p className="muted">{t('share.readonly')}</p>
        {payload ? <p className="muted">{timestamp(payload.createdAt)}</p> : null}
        <a className="btn btn--ghost btn--sm" href="#/test">
          {t('share.openTest')}
        </a>
      </div>

      {loading ? <p className="muted">{t('share.loading')}</p> : null}
      {error ? <EmptyState>{error}</EmptyState> : null}

      {payload?.kind === 'run' ? <SharedRun payload={payload} /> : null}
      {payload?.kind === 'sweep' ? <SharedSweep payload={payload} /> : null}
    </div>
  );
}
